import { get } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import { Checkbox, InputIncrement } from '@commons/utils/styledLibraryComponents';
import {
  Image,
  ItemNameContainer,
  Name,
} from '@commons/TableForm/TableFormInModal/styledComponents';
import DisplayNumber, { formatNumber } from '@commons/DisplayNumber';

import { DeleteButton } from '../styledComponents';

const renderNameCell = (params) => {
  const item = params.row;

  const placeholder = item.isIngredient
    ? '/images/inpulse/ingredient-photo-placeholder.svg'
    : '/images/inpulse/recipe-photo-placeholder.svg';

  const handleClick = () => {
    const adminIngredientPath = '/admin/products/ingredients';
    const adminRecipePath = item.isKitchen
      ? '/admin/kitchen-products/recipes'
      : '/admin/products/recipes';

    const basePath = item.isIngredient ? adminIngredientPath : adminRecipePath;

    window.open(`${basePath}/${get(item, '__entityId')}/details`, '_blank');
  };

  return (
    <ItemNameContainer isLink onClick={handleClick}>
      <Image src={item.img || placeholder} />
      <Name>{item.name}</Name>
    </ItemNameContainer>
  );
};

// TODO UPDATE THIS TO ALLOW MORE THAN 2 CHANNELS
const renderCostCell = (
  params,
  clientDefaultChannelId,
  currency,
  channels,
  hasMultipleChannels,
) => {
  const row = params.row;

  if (row.isIngredient) {
    const totalCost = row.cost * row.quantity;
    return <DisplayNumber number={totalCost || null} />;
  }

  if (!hasMultipleChannels || !row.hasMultipleChannels) {
    const totalCost = row.costByChannelId[clientDefaultChannelId] * row.quantity;
    return <DisplayNumber number={totalCost || null} />;
  }

  return channels
    .map((channel) => {
      if (!row.costByChannelId[channel.id]) {
        return '-';
      }
      return formatNumber(row.costByChannelId[channel.id] * row.quantity, currency.numberDecimals);
    })
    .join(' | ');
};

const renderCheckboxCell = (params, currentChannel, channels, onRowUpdate) => {
  const row = params.row;

  const channelsWithoutCurrent = channels.filter(({ id }) => id !== currentChannel.id);
  const areAllOtherChannelsUnchecked = channelsWithoutCurrent.every(({ id }) => !row.channels[id]);

  const handleClick = () => {
    if (areAllOtherChannelsUnchecked) {
      return;
    }

    onRowUpdate({
      ...row,
      channels: { ...row.channels, [currentChannel.id]: !row.channels[currentChannel.id] },
    });
  };

  return (
    <Checkbox
      handleClick={handleClick}
      isChecked={!!row.channels[currentChannel.id]}
      isDisabled={areAllOtherChannelsUnchecked}
      shape="square"
      noSize
    />
  );
};

const handleSetValue = (row, onRowUpdate, newValue) => {
  onRowUpdate({ ...row, quantity: newValue });
};

const renderQuantityCell = (params, onRowUpdate, isReadOnly) => {
  const row = params.row;

  return (
    <InputIncrement
      isDisabled={isReadOnly}
      setValue={(newValue) => handleSetValue(row, onRowUpdate, newValue)}
      value={row.quantity}
      enableNullValue
    />
  );
};

const renderDeleteCell = (params, onDelete) => {
  const handleClick = () => {
    onDelete(params.row.id);
  };

  return (
    <DeleteButton type="button" onClick={handleClick}>
      <img alt="delete" src="/images/inpulse/close-black-small.svg" />
    </DeleteButton>
  );
};

export const getDatagridColumns = ({
  clientDefaultChannelId,
  currency,
  recipe,
  onRowUpdate,
  onDelete,
  isReadOnly,
  channels,
}) => {
  const columns = [
    {
      field: 'name',
      headerName: i18next.t('GENERAL.NAME'),
      width: 360,
      sortable: false,
      renderCell: renderNameCell,
    },
    {
      field: 'category',
      headerName: i18next.t('GENERAL.CATEGORY'),
      width: 130,
      sortable: false,
    },
    {
      field: 'quantity',
      headerName: i18next.t('GENERAL.QUANTITY'),
      width: 160,
      sortable: false,
      renderCell: (params) => renderQuantityCell(params, onRowUpdate, isReadOnly),
    },
    {
      field: 'unit',
      headerName: i18next.t('GENERAL.UNIT'),
      width: 70,
      sortable: false,
    },
    {
      field: 'cost',
      headerName: i18next.t('GENERAL.COST_EX_TAX_WITH_CURRENCY', {
        currency: currency.alphabeticCode,
      }),
      width: 160,
      sortable: false,
      renderCell: (params) =>
        renderCostCell(
          params,
          clientDefaultChannelId,
          currency,
          channels,
          recipe.hasMultipleChannels,
        ),
    },
  ];

  if (recipe.hasMultipleChannels) {
    channels.forEach((channel) => {
      columns.push({
        field: channel.id,
        headerName: channel.fullName,
        minWidth: 110,
        sortable: false,
        renderCell: (params) => renderCheckboxCell(params, channel, channels, onRowUpdate),
      });
    });
  }

  if (!isReadOnly) {
    columns.push({
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => renderDeleteCell(params, onDelete),
    });
  }

  return columns;
};
